import React, { useEffect, useMemo } from "react";
import { MapContainer, TileLayer, CircleMarker, Tooltip, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";

const PAKISTAN_CENTER = [30.3753, 69.3451];

// Circle size = brick kilns, colour = rescued families (light → dark green)
const radiusFor = (kilns, maxKilns) =>
  6 + 18 * Math.sqrt(kilns / Math.max(maxKilns, 1));

const colorFor = (rescued, maxRescued) => {
  if (!rescued) return "#9e9e9e";
  const ratio = rescued / Math.max(maxRescued, 1);
  if (ratio > 0.66) return "#04501f";
  if (ratio > 0.33) return "#077834";
  return "#3fae63";
};

// Zoom the map to the cities that have a location
function FitToCities({ cities }) {
  const map = useMap();

  useEffect(() => {
    if (!cities.length) return;
    if (cities.length === 1) {
      map.setView([cities[0].lat, cities[0].lng], 9);
      return;
    }
    map.fitBounds(
      cities.map((c) => [c.lat, c.lng]),
      { padding: [30, 30] }
    );
  }, [cities, map]);

  return null;
}

export default function CityMap({ cities = [], loading = false }) {
  const located = useMemo(
    () => cities.filter((c) => c.lat !== null && c.lng !== null),
    [cities]
  );
  const unlocated = cities.filter((c) => c.lat === null || c.lng === null);

  const maxKilns = Math.max(0, ...cities.map((c) => c.brick_kilns));
  const maxRescued = Math.max(0, ...cities.map((c) => c.rescued_families));

  return (
    <div className="mc-card">
      <div className="mc-card-header">
        <h4 className="mc-card-title">City Map - Brick Kilns & Rescued Families</h4>
      </div>

      <MapContainer
        center={PAKISTAN_CENTER}
        zoom={5}
        scrollWheelZoom={false}
        style={{ height: "450px", width: "100%", borderRadius: 8 }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitToCities cities={located} />

        {located.map((city) => (
          <CircleMarker
            key={city.id}
            center={[city.lat, city.lng]}
            radius={radiusFor(city.brick_kilns, maxKilns)}
            pathOptions={{
              color: "#ffffff",
              weight: 1.5,
              fillColor: colorFor(city.rescued_families, maxRescued),
              fillOpacity: 0.8,
            }}
          >
            <Tooltip direction="top" offset={[0, -6]}>
              <div style={{ fontWeight: 700, marginBottom: 2 }}>{city.name}</div>
              <div>Brick Kilns: <b>{city.brick_kilns}</b></div>
              <div>Rescued Families: <b>{city.rescued_families}</b></div>
            </Tooltip>
          </CircleMarker>
        ))}
      </MapContainer>

      <div
        className="d-flex flex-wrap align-items-center gap-3 mt-2"
        style={{ fontSize: 13 }}
      >
        <span>Circle size = number of brick kilns</span>
        <span className="d-flex align-items-center gap-1">
          Rescued families:
          {["#9e9e9e", "#3fae63", "#077834", "#04501f"].map((c) => (
            <span
              key={c}
              style={{
                display: "inline-block",
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor: c,
              }}
            />
          ))}
          <span>(none → most)</span>
        </span>
        {loading && <span className="text-muted">Loading...</span>}
        {!loading && !cities.length && (
          <span className="text-muted">No cities with brick kilns found.</span>
        )}
      </div>

      {unlocated.length > 0 && (
        <div className="text-muted mt-1" style={{ fontSize: 12 }}>
          No location saved for: {unlocated.map((c) => c.name).join(", ")}
        </div>
      )}
    </div>
  );
}
